<template>
  <div class="bg-surface min-h-screen text-on-surface flex flex-col">
    <main class="w-full max-w-[1440px] mx-auto px-8 pt-40 pb-24 portal-home-font-2x flex-1">
      <div class="max-w-[1200px] mx-auto relative z-10">
        <div class="mb-12 inline-flex items-center gap-2 bg-surface-container/50 px-5 py-2 rounded-full text-sm font-body tracking-wide relative z-20">
          <a class="text-on-surface-variant hover:text-primary transition-colors" href="#" @click.prevent="goHomeSection('home')">首页</a>
          <span class="material-symbols-outlined text-[14px] text-outline-variant/60">chevron_right</span>
          <span class="text-primary font-medium">新闻公告中心</span>
        </div>

        <div class="flex items-center gap-4 border-l-[6px] border-secondary pl-6">
          <h1 class="font-headline text-4xl font-bold text-primary tracking-tight">新闻公告中心</h1>
        </div>

        <div class="mt-16 flex gap-12 border-b border-surface-variant overflow-x-auto no-scrollbar">
          <button
            v-for="item in categoryOptions"
            :key="item.value"
            :class="tabClass(item.value)"
            type="button"
            @click="switchCategory(item.value)"
          >
            {{ item.label }}
          </button>
        </div>

        <div class="mt-12">
          <div v-if="loading" class="py-32 text-center text-on-surface-variant text-xl">加载中...</div>
          <div v-else-if="items.length === 0" class="py-32 text-center text-on-surface-variant text-xl">暂无内容</div>
          <div v-else class="flex flex-col">
            <article
              v-for="item in items"
              :key="item.contentId"
              class="group -mx-6 flex cursor-pointer items-start justify-between gap-10 rounded-md border-b border-surface-variant px-6 py-8 transition-colors hover:bg-surface-container-low"
              @click="goDetail(item)"
            >
              <div class="min-w-0 flex flex-1 items-center gap-4">
                <span
                  v-if="item.isTop === '1'"
                  class="shrink-0 rounded-md bg-secondary-container px-4 py-1.5 text-base font-bold text-on-secondary-container"
                >
                  置顶
                </span>
                <h2 class="min-w-0 font-headline text-lg font-semibold text-primary transition-colors group-hover:text-secondary">
                  {{ item.title }}
                </h2>
              </div>
              <span class="shrink-0 pt-1 text-base text-outline">{{ formatPortalDate(item.publishTime) }}</span>
            </article>
          </div>
        </div>

        <div v-if="total > pageSize" class="mt-20 flex items-center justify-center gap-3">
          <button
            class="flex h-10 w-10 items-center justify-center rounded-md text-on-surface-variant transition-colors hover:bg-surface-container-high disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="pageNum <= 1"
            type="button"
            @click="changePage(pageNum - 1)"
          >
            <span class="material-symbols-outlined text-base">chevron_left</span>
          </button>
          <template v-for="item in paginationItems">
            <span
              v-if="item.type === 'ellipsis'"
              :key="item.key"
              class="px-2 text-on-surface-variant text-lg"
            >
              ...
            </span>
            <button
              v-else
              :key="`page-btn-${item.key}`"
              :class="pageItemClass(item.page)"
              type="button"
              @click="changePage(item.page)"
            >
              {{ item.page }}
            </button>
          </template>
          <button
            class="flex h-10 w-10 items-center justify-center rounded-md text-on-surface-variant transition-colors hover:bg-surface-container-high disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="pageNum >= totalPages"
            type="button"
            @click="changePage(pageNum + 1)"
          >
            <span class="material-symbols-outlined text-base">chevron_right</span>
          </button>
        </div>
      </div>
    </main>

    <portal-footer />
  </div>
</template>

<script>
import { fetchPortalContentList, formatPortalDate } from '@/utils/content'
import PortalFooter from '@/components/PortalFooter.vue'

export default {
  name: 'ContentList',
  components: {
    PortalFooter
  },
  data () {
    return {
      loading: false,
      category: this.$route.query.category === 'notice' ? 'notice' : 'news',
      pageNum: 1,
      pageSize: 10,
      total: 0,
      items: [],
      categoryOptions: [
        { label: '协会新闻', value: 'news' },
        { label: '官方公告', value: 'notice' }
      ]
    }
  },
  computed: {
    currentCategoryLabel () {
      return this.category === 'notice' ? '官方公告' : '协会新闻'
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    },
    paginationItems () {
      const items = []
      const totalPages = this.totalPages

      if (totalPages <= 1) {
        return [{ type: 'page', page: 1, key: 'page-1' }]
      }

      items.push({ type: 'page', page: 1, key: 'page-1' })

      const start = Math.max(2, this.pageNum - 1)
      const end = Math.min(totalPages - 1, this.pageNum + 2)

      if (start > 2) {
        items.push({ type: 'ellipsis', key: 'ellipsis-left' })
      }

      for (let page = start; page <= end; page += 1) {
        items.push({ type: 'page', page, key: `page-${page}` })
      }

      if (end < totalPages - 1) {
        items.push({ type: 'ellipsis', key: 'ellipsis-right' })
      }

      items.push({ type: 'page', page: totalPages, key: `page-${totalPages}` })

      return items
    }
  },
  watch: {
    '$route.query.category': {
      immediate: false,
      handler (value) {
        const nextCategory = value === 'notice' ? 'notice' : 'news'
        if (nextCategory !== this.category) {
          this.category = nextCategory
          this.pageNum = 1
          this.loadList()
        }
      }
    }
  },
  mounted () {
    this.loadList()
  },
  methods: {
    formatPortalDate,
    async loadList () {
      this.loading = true
      try {
        const data = await fetchPortalContentList({
          category: this.category,
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        this.items = data.rows || []
        this.total = data.total || 0
      } catch (error) {
        console.error('加载内容列表失败', error)
        this.items = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    tabClass (value) {
      if (value === this.category) {
        return 'pb-3 border-b-2 border-secondary text-primary font-semibold text-lg transition-colors'
      }
      return 'pb-3 border-b-2 border-transparent text-on-surface-variant font-medium text-lg transition-colors hover:text-primary'
    },
    switchCategory (category) {
      if (category === this.category) {
        return
      }
      this.category = category
      this.pageNum = 1
      this.$router.push({ path: '/content/list', query: { category } })
      this.loadList()
    },
    pageItemClass (page) {
      if (page === this.pageNum) {
        return 'flex h-8 w-8 items-center justify-center rounded-md bg-primary text-on-primary font-medium text-sm'
      }
      return 'flex h-8 w-8 items-center justify-center rounded-md text-on-surface-variant font-medium text-sm transition-colors hover:bg-surface-container-high'
    },
    changePage (pageNum) {
      if (pageNum < 1 || pageNum > this.totalPages || pageNum === this.pageNum) {
        return
      }
      this.pageNum = pageNum
      this.loadList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    goDetail (item) {
      if (item.displayMode === 'link' && item.externalUrl) {
        window.open(item.externalUrl, '_blank')
        return
      }
      this.$router.push(`/content/${item.contentId}`)
    },
    goHomeSection (section) {
      this.$router.push({ path: '/home', query: { section } })
    },
    goComingSoon () {
      this.$router.push('/coming-soon')
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/ContentList.vue